import { createApp } from "vue";

import App from "./App.vue";
import router from "./router";
import store from "./store";
import VueKeycloakJs from "@dsb-norge/vue-keycloak-js";
import { initAuthOnLoad, initAuthConfig } from "./utils/auth";
import globalComponents from "./global-components";
import utils from "./utils";
import "./libs";
import "./utils/auth";
import getUserInfo from "./utils/get-user-info.js";
import "./utils/axios-interceptor";
import { checkThaidLinkStatus } from "./utils/thaid-link.js";
import getThaidToken from "./utils/get-thaid-token.js";
import "./utils/axios-interceptor";

import Swal from "sweetalert2";
import VueMatomo from "vue-matomo";

// vue-select css
import "vue-select/dist/vue-select.css";

// SASS Theme
import "./assets/sass/app.scss";

// ─── ThaID First Login Error Detection ────────────────────────────────────────
// เมื่อ login ThaID แต่ยังไม่เคยเชื่อมกับ Account ปกติ
// Keycloak จะ redirect กลับมาด้วย #error=login_required
// ต้องจับก่อน Keycloak-JS init เพราะ keycloak-js จะ parse hash แล้วลบออก
//
// สำคัญ: check-sso (onLoad: 'check-sso') ก็ส่ง #error=login_required กลับมาเหมือนกัน
// เมื่อยังไม่ได้ login — ต้องแยกให้ออกว่ามาจาก ThaID broker หรือ check-sso
//
// วิธีแยก: ใช้ sessionStorage flag "mso-app-loaded"
// - flag ถูกตั้งทุกครั้งที่ app โหลดจาก URL ปกติ (ไม่มี error hash)
// - flag ค้างใน sessionStorage ตลอด tab session (ไม่ลบ)
// - ถ้า IIFE เจอ #error=login_required + มี flag → เป็น redirect กลับจาก check-sso → ข้ามไป
// - ถ้า IIFE เจอ #error=login_required + ไม่มี flag → เป็น redirect จาก error.ftl (ThaID broker)
//   ใน tab ใหม่ที่ไม่เคยโหลด app มาก่อน → แสดง SweetAlert
const thaidBlocked = (() => {
  const hash = window.location.hash;

  // ─── ไม่มี error hash → app กำลังโหลดปกติ ───
  if (!hash || !hash.includes("error=login_required")) {
    try {
      sessionStorage.setItem("mso-app-loaded", "1");
    } catch (e) { /* sessionStorage disabled */ }
    return false;
  }

  // ─── hash มี error=login_required ───

  // ถ้า hash มี code= ด้วย → login สำเร็จ → ข้ามไป
  if (hash.includes("code=")) return false;

  // ถ้า app เคยโหลดใน tab นี้แล้ว → เป็น check-sso ปกติ หรือ keycloak-js retry → ข้ามไป
  // (flag ไม่ถูกลบ — ค้างตลอด tab session)
  try {
    if (sessionStorage.getItem("mso-app-loaded")) {
      return false;
    }
  } catch (e) {
    return false;
  }

  // Tab ใหม่ + เจอ #error=login_required ทันที → มาจาก ThaID broker (error.ftl redirect)
  window.history.replaceState(null, "", window.location.pathname);
  Swal.fire({
    title: "บัญชี ThaID ยังไม่ได้เชื่อมกับระบบ",
    html: "กรุณาเข้าสู่ระบบด้วย <b>ชื่อผู้ใช้และรหัสผ่าน</b> ก่อน<br>จากนั้นไปที่หน้า <b>จัดการข้อมูลผู้ใช้งาน</b> เพื่อเชื่อมบัญชี ThaID",
    icon: "warning",
    confirmButtonText: "เข้าสู่ระบบด้วยชื่อผู้ใช้",
    confirmButtonColor: "#1C3FAA",
    allowOutsideClick: false,
  }).then(() => {
    window.location.replace(window.location.origin);
  });
  return true;
})();

// Fontawesome
import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import {
  faCircle,
  faChevronLeft,
  faChevronRight,
  faCaretDown,
  faSpinner,faCircleNotch 
} from "@fortawesome/free-solid-svg-icons";
library.add(faCircle, faChevronLeft, faChevronRight, faCaretDown, faSpinner, faCircleNotch );

if (!thaidBlocked) {

createApp(App).mixin({
  methods: {
    globalNumDecFixed: function(num, dec_amount) {
      if (!(num % 1)) {
        dec_amount = 0;
      }
      return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: dec_amount,
      }).format(Number.parseFloat(num).toFixed(dec_amount));
    },

    globalRandom: function(max_ran, min_ran, dec_amount = 0) {
      var new_ran = Math.random() * (max_ran - min_ran) + min_ran;
      new_ran = new_ran + Math.random() * 99999 * 0.00001;
      return this.globalNumDecFixed(new_ran, dec_amount);
    },

    millionShortNum: function(num) {
      if (num >= 1000000000000000000000000) {
        return this.globalNumDecFixed(
          (num / 1000000000000000000000000).toFixed(2).replace(/\.0$/, ""),
          2
        );
      }
      if (num >= 1000000000000000000) {
        return this.globalNumDecFixed(
          (num / 1000000000000000000).toFixed(2).replace(/\.0$/, ""),
          2
        );
      }
      if (num >= 1000000000000) {
        return this.globalNumDecFixed(
          (num / 1000000000000).toFixed(2).replace(/\.0$/, ""),
          2
        );
      }
      if (num >= 1000000) {
        return this.globalNumDecFixed(
          (num / 1000000).toFixed(2).replace(/\.0$/, ""),
          2
        );
      }
      if (num < 1000000) {
        return this.globalNumDecFixed(
          (num / 1000000).toFixed(2).replace(/\.0$/, ""),
          3
        );
      }
      return this.globalNumDecFixed(num.toFixed(2).replace(/\.0$/, ""), 2);
    },

    millionShortString: function(num) {
      if (num >= 1000000000000000000000000) {
        return "ล้านล้านล้านล้าน";
      }
      if (num >= 1000000000000000000) {
        return "ล้านล้านล้าน";
      }
      if (num >= 1000000000000) {
        return "ล้านล้าน";
      }
      if (num >= 1000000) {
        return "ล้าน";
      }
      return "";
    },
  },
});

const app = createApp(App)
  .use(store)
  .use(router);

app.use(VueMatomo, {
  host: "https://montana.emenscr.in.th/analaytic/matomo",
  siteId: 3,
  trackerFileName: "matomo",
  router: router,
  enableLinkTracking: true,
  requireConsent: false,
  trackInitialView: true,
  disableCookies: false,
  requireCookieConsent: false,
  enableHeartBeatTimer: true,
  heartBeatTimerInterval: 15,
  debug: false,
  userId: undefined,
  cookieDomain: undefined,
  domains: undefined,
  preInitActions: [],
  trackSiteSearch: false,
});

app.use(VueKeycloakJs, {
  init: initAuthOnLoad,
  config: initAuthConfig,
  logout: {
    redirectUri: "window.location.origin",
  },
  onReady(keycloak) {
    localStorage.removeItem("mso_kc_retry");
    const isDev = process.env.VUE_APP_ENV !== "production";
    if (isDev) {
      console.log("[DEBUG Keycloak] authenticated:", keycloak.authenticated);
      console.log("[DEBUG Keycloak] subject:", keycloak.subject);
      console.log("[DEBUG Keycloak] sessionId:", keycloak.sessionId);
      console.log("[DEBUG Keycloak] tokenParsed:", JSON.parse(JSON.stringify(keycloak.tokenParsed || {})));
      console.log("[DEBUG Keycloak] idTokenParsed:", JSON.parse(JSON.stringify(keycloak.idTokenParsed || {})));
      console.log("[DEBUG Keycloak] realmAccess:", keycloak.realmAccess);
      console.log("[DEBUG Keycloak] resourceAccess:", keycloak.resourceAccess);
      console.log("[DEBUG Keycloak] token (JWT):", keycloak.token);
      console.log("[DEBUG Keycloak] idToken (JWT):", keycloak.idToken);
      console.log("[DEBUG Keycloak] refreshToken:", keycloak.refreshToken);
    }
    localStorage.removeItem("mso_kc_retry");
    store.dispatch("userAuth/setStore", keycloak);

    if (keycloak.authenticated) {
      window._paq.push(["setUserId", keycloak.tokenParsed.preferred_username]);

      // Check ThaID link status and store in Vuex
      checkThaidLinkStatus()
        .then(status => {
          console.log("[DEBUG ThaID] link status:", status);
          store.dispatch("userAuth/setThaidLinkStatus", status);
        })
        .catch(err => {
          console.log("[DEBUG ThaID] link status check error:", err.message);
        });

      // Fetch raw ThaID token from Keycloak Broker API (debug only)
      // ถ้า user login ด้วย ThaID → จะได้ access_token, id_token จาก ThaID
      // ถ้า user login ปกติ (ไม่ผ่าน ThaID) → จะได้ null
      getThaidToken()
        .then(thaidToken => {
          console.log("[DEBUG ThaID] raw token:", thaidToken);
        })
        .catch(err => {
          console.log("[DEBUG ThaID] raw token error:", err.message);
        });
    }

    setInterval(() => {
      keycloak
        .updateToken(70)
        .then((refreshed) => {
          if (refreshed) {
            store.dispatch("userAuth/setToken", keycloak.token);
          }
        })
        .catch(() => {
          console.log("[DEBUG Keycloak] Failed to refresh token");
        });
    }, 10000);

    getUserInfo();
  },
  onInitError(error, err) {
    const MAX_RETRIES = 3;
    const retryCount = parseInt(localStorage.getItem("mso_kc_retry") || "0", 10);
    console.error(`Keycloak init error (attempt ${retryCount + 1}/${MAX_RETRIES}):`, error, err);
    if (retryCount < MAX_RETRIES) {
      localStorage.setItem("mso_kc_retry", String(retryCount + 1));
      location.reload();
    } else {
      localStorage.removeItem("mso_kc_retry");
    }
  },
});

globalComponents(app);
utils(app);

app.component("font-awesome-icon", FontAwesomeIcon).mount("#app");

} // end if (!thaidBlocked)
